<template>
  <div class="date-picker" ref="root">
    <button type="button" ref="button" class="date-picker-button" @click="toggleOpen">
      {{ buttonLabel }}
    </button>

    <div v-if="isOpen" ref="popover" class="date-picker-popover">
      <div v-if="showModeToggle" class="date-picker-toolbar">
        <button
          type="button"
          class="date-picker-mode"
          :class="{ active: mode === 'single' }"
          @click="setMode('single')"
        >
          День
        </button>
        <button
          type="button"
          class="date-picker-mode"
          :class="{ active: mode === 'range' }"
          @click="setMode('range')"
        >
          Период
        </button>
      </div>
      <input ref="calendarInput" class="date-picker-input" />
    </div>
  </div>
</template>

<script>
import flatpickr from "flatpickr";
import "flatpickr/dist/flatpickr.min.css";
import { Russian } from "flatpickr/dist/l10n/ru.js";
import dayjs from "dayjs";

export default {
  emits: ["input"],
  props: {
    valueFrom: { type: String, required: true },
    valueTo: { type: String, required: true },
    selectionMode: {
      type: String,
      default: "",
    },
    showModeToggle: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      datePicker: null,
      isOpen: false,
      mode: "single",
      pendingFrom: "",
      pendingTo: "",
    };
  },
  computed: {
    buttonLabel() {
      if (this.valueFrom === this.valueTo) {
        return this.formatSingleLabel(this.valueFrom);
      }

      return `${this.formatShortLabel(this.valueFrom)} - ${this.formatShortLabel(
        this.valueTo
      )}`;
    },
  },
  methods: {
    toggleOpen() {
      this.isOpen = !this.isOpen;

      if (this.isOpen) {
        this.$nextTick(() => {
          this.setupPicker();
        });
      }
    },
    close() {
      this.isOpen = false;
      if (this.datePicker) {
        this.datePicker.destroy();
        this.datePicker = null;
      }
    },
    handleDocumentClick(event) {
      if (!this.isOpen || !this.$refs.root) {
        return;
      }

      if (!this.$refs.root.contains(event.target)) {
        this.close();
      }
    },
    setMode(mode) {
      if (!this.showModeToggle) {
        return;
      }

      if (this.mode === mode) {
        return;
      }

      this.mode = mode;
      if (this.datePicker) {
        this.datePicker.set("mode", mode === "single" ? "single" : "range");
        this.syncPickerValue();
      }
    },
    setupPicker() {
      if (this.datePicker) {
        this.datePicker.destroy();
      }

      this.mode =
        this.selectionMode ||
        (this.valueFrom === this.valueTo ? "single" : "range");
      this.pendingFrom = this.valueFrom;
      this.pendingTo = this.valueTo;
      this.datePicker = flatpickr(this.$refs.calendarInput, {
        inline: true,
        mode: this.mode === "single" ? "single" : "range",
        locale: Russian,
        dateFormat: "Y-m-d",
        appendTo: this.$refs.popover,
        defaultDate:
          this.valueFrom === this.valueTo
            ? this.valueFrom
            : [this.valueFrom, this.valueTo],
        minDate: dayjs().subtract(2, "month").format("YYYY-MM-DD"),
        maxDate: dayjs().add(1, "day").format("YYYY-MM-DD"),
        monthSelectorType: "static",
        disableMobile: true,
        onChange: this.handleChange,
      });
    },
    syncPickerValue() {
      if (!this.datePicker) {
        return;
      }

      const value =
        this.mode === "single" || this.valueFrom === this.valueTo
          ? this.valueFrom
          : [this.valueFrom, this.valueTo];

      this.datePicker.setDate(value, true);
    },
    handleChange(selectedDates) {
      if (!selectedDates.length) {
        return;
      }

      if (this.mode === "single") {
        const date = dayjs(selectedDates[0]).format("YYYY-MM-DD");
        this.pendingFrom = date;
        this.pendingTo = date;
        this.$emit("input", { from: date, to: date });
        this.close();
        return;
      }

      if (selectedDates.length === 2) {
        const from = dayjs(selectedDates[0]).format("YYYY-MM-DD");
        const to = dayjs(selectedDates[1]).format("YYYY-MM-DD");
        this.pendingFrom = from;
        this.pendingTo = to;
        this.$emit("input", { from, to });
        this.close();
      }
    },
    isDateToday(dateStr) {
      return dayjs(dateStr).format("YYYY-MM-DD") === dayjs().format("YYYY-MM-DD");
    },
    isDateYesterday(dateStr) {
      return (
        dayjs(dateStr).format("YYYY-MM-DD") ===
        dayjs().subtract(1, "day").format("YYYY-MM-DD")
      );
    },
    formatSingleLabel(dateStr) {
      if (this.isDateToday(dateStr)) {
        return "Сегодня";
      }

      if (this.isDateYesterday(dateStr)) {
        return "Вчера";
      }

      return new Intl.DateTimeFormat("ru-RU", {
        month: "long",
        day: "numeric",
      }).format(new Date(dayjs(dateStr).valueOf()));
    },
    formatShortLabel(dateStr) {
      return new Intl.DateTimeFormat("ru-RU", {
        month: "long",
        day: "numeric",
      }).format(new Date(dayjs(dateStr).valueOf()));
    },
  },
  mounted() {
    document.addEventListener("click", this.handleDocumentClick);
  },
  beforeUnmount() {
    document.removeEventListener("click", this.handleDocumentClick);
    if (this.datePicker) {
      this.datePicker.destroy();
    }
  },
  watch: {
    valueFrom() {
      this.syncPickerValue();
    },
    valueTo() {
      this.syncPickerValue();
    },
    selectionMode(newValue) {
      if (!newValue) {
        return;
      }

      this.mode = newValue;
      if (this.datePicker) {
        this.datePicker.set("mode", newValue === "single" ? "single" : "range");
        this.syncPickerValue();
      }
    },
  },
};
</script>

<style scoped>
.date-picker {
  position: relative;
}

.date-picker-button {
  min-width: 132px;
}

.date-picker-popover {
  position: absolute;
  top: calc(100% + 10px);
  right: 0;
  z-index: 120;
  padding: 12px;
  border: 1px solid rgba(255, 255, 255, 0.22);
  border-radius: 8px;
  background: rgba(6, 42, 69, 0.94);
  box-shadow: 0 20px 48px rgba(0, 0, 0, 0.32);
  backdrop-filter: blur(16px) saturate(150%);
  -webkit-backdrop-filter: blur(16px) saturate(150%);
}

.date-picker-toolbar {
  display: inline-flex;
  gap: 6px;
  margin-bottom: 10px;
}

.date-picker-mode {
  min-height: 28px;
  padding: 4px 10px;
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 8px;
  font-family: "Evolventa", sans-serif;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.82);
  background: rgba(255, 255, 255, 0.06);
}

.date-picker-mode.active {
  color: #ffffff;
  background: rgba(255, 255, 255, 0.14);
  border-color: rgba(255, 255, 255, 0.3);
}

.date-picker-input {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}

:deep(.flatpickr-calendar) {
  width: 307px;
  border: 0;
  border-radius: 0;
  background: transparent;
  box-shadow: none;
}

:deep(.flatpickr-months) {
  margin-bottom: 8px;
}

:deep(.flatpickr-month) {
  color: #ffffff;
  fill: #ffffff;
}

:deep(.flatpickr-current-month) {
  font-family: "Evolventa", sans-serif;
  font-size: 14px;
  padding-top: 3px;
}

:deep(.flatpickr-current-month .flatpickr-monthDropdown-months) {
  font-weight: bold;
  color: #ffffff;
  background: transparent;
}

:deep(.flatpickr-current-month .numInputWrapper) {
  display: none !important;
}

:deep(.flatpickr-current-month .cur-year),
:deep(.flatpickr-current-month .arrowUp),
:deep(.flatpickr-current-month .arrowDown) {
  display: none !important;
}

:deep(span.flatpickr-weekday) {
  color: rgba(255, 255, 255, 0.55);
  font-family: "Evolventa", sans-serif;
  font-size: 12px;
  font-weight: normal;
}

:deep(.flatpickr-day) {
  border-radius: 8px;
  color: rgba(255, 255, 255, 0.88);
}

:deep(.flatpickr-day:hover) {
  background: rgba(255, 255, 255, 0.08);
  border-color: transparent;
}

:deep(.flatpickr-day.today) {
  border-color: rgba(255, 255, 255, 0.38);
}

:deep(.flatpickr-day.selected),
:deep(.flatpickr-day.startRange),
:deep(.flatpickr-day.endRange) {
  background: rgba(255, 255, 255, 0.22);
  border-color: rgba(255, 255, 255, 0.22);
}

:deep(.flatpickr-day.inRange) {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.1);
  box-shadow: -5px 0 0 rgba(255, 255, 255, 0.1),
    5px 0 0 rgba(255, 255, 255, 0.1);
}

:deep(.flatpickr-day.flatpickr-disabled),
:deep(.flatpickr-day.flatpickr-disabled:hover) {
  color: rgba(255, 255, 255, 0.22);
}
</style>
